import React from 'react';
import { typography } from './tokens';

/**
 * Typography utility functions for applying design system typography variants
 */

export type TypographyVariant = keyof typeof typography.variants;

/**
 * Get CSS style object for a typography variant
 */
export const getTypographyStyles = (variant: TypographyVariant): React.CSSProperties => {
  return typography.variants[variant];
};

/**
 * Typography component props
 */
export interface TypographyProps {
  variant: TypographyVariant;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  as?: keyof JSX.IntrinsicElements;
}

/**
 * Typography component for consistent text styling throughout the application
 */
export const Typography: React.FC<TypographyProps> = ({
  variant,
  children,
  className = '',
  style = {},
  as: Component = 'div',
}) => {
  const variantStyles = getTypographyStyles(variant);
  
  return (
    <Component
      className={className}
      style={{ ...variantStyles, ...style }}
    >
      {children}
    </Component>
  );
};

/**
 * Predefined typography components for common use cases
 */
export const Title: React.FC<Omit<TypographyProps, 'variant' | 'as'>> = (props) => (
  <Typography {...props} variant="title" as="h1" />
);

export const H1: React.FC<Omit<TypographyProps, 'variant' | 'as'>> = (props) => (
  <Typography {...props} variant="h1" as="h1" />
);

export const H2: React.FC<Omit<TypographyProps, 'variant' | 'as'>> = (props) => (
  <Typography {...props} variant="h2" as="h2" />
);

export const H3: React.FC<Omit<TypographyProps, 'variant' | 'as'>> = (props) => (
  <Typography {...props} variant="h3" as="h3" />
);

export const Label: React.FC<Omit<TypographyProps, 'variant' | 'as'>> = (props) => (
  <Typography {...props} variant="label" as="span" />
);

export const BodyText: React.FC<Omit<TypographyProps, 'variant' | 'as'>> = (props) => (
  <Typography {...props} variant="cardBody" as="p" />
);

export const Caption: React.FC<Omit<TypographyProps, 'variant' | 'as'>> = (props) => (
  <Typography {...props} variant="cardCaption" as="span" />
);

export const LedgerLabel: React.FC<Omit<TypographyProps, 'variant' | 'as'>> = (props) => (
  <Typography {...props} variant="ledgerLabel" as="span" />
);

export const LedgerNumbers: React.FC<Omit<TypographyProps, 'variant' | 'as'>> = (props) => (
  <Typography {...props} variant="ledgerNumbers" as="span" />
);